// src/components/admin/DeleteVariantModal.tsx
import React from 'react';
import { X, AlertTriangle, Image as ImageIcon } from 'lucide-react';

interface DeleteVariantModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (moveImagesToDefault: boolean) => void;
    variantName: string;
    imagesCount: number;
    willHaveOneVariantAfterDelete: boolean;
}

const DeleteVariantModal: React.FC<DeleteVariantModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    variantName,
    imagesCount,
    willHaveOneVariantAfterDelete
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
                
                <div className="relative bg-white rounded-lg max-w-md w-full p-6">
                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                    >
                        <X size={20} />
                    </button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-red-100 rounded-full">
                            <AlertTriangle size={24} className="text-red-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">
                            Delete Variant
                        </h3>
                    </div>

                    <p className="text-gray-600 mb-4">
                        Are you sure you want to delete <span className="font-medium">"{variantName}"</span>?
                    </p>

                    {imagesCount > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <ImageIcon size={18} className="text-gray-500 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">
                                        This variant has {imagesCount} image(s)
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        What would you like to do with these images?
                                    </p>
                                </div>
                            </div>
                            
                            <div className="mt-3 space-y-2">
                                <button
                                    onClick={() => onConfirm(true)}
                                    className="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <div className="font-medium text-gray-700">Move to default images</div>
                                    <div className="text-xs text-gray-500">
                                        Images will become product-wide default images
                                    </div>
                                </button>
                                
                                <button
                                    onClick={() => onConfirm(false)}
                                    className="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <div className="font-medium text-gray-700">Delete images</div>
                                    <div className="text-xs text-gray-500">
                                        Images will be permanently deleted with the variant
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {willHaveOneVariantAfterDelete && (
                        <div className="bg-blue-50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-blue-700">
                                <span className="font-medium">Note:</span> After deletion, you'll have only one variant left. 
                                Products with one variant use default images only. Any remaining variant-specific images 
                                will be moved to default.
                            </p>
                        </div>
                    )}

                    {imagesCount === 0 && (
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onConfirm(false)}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete Variant
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteVariantModal;